import { gql } from '@apollo/client'
import { Card, Chip, ScalesIcon, UserMultiIcon } from '@farol-ds/react'
import classNames from 'classnames'
import { HTMLAttributes } from 'react'

import { BenefitsList } from '../benefits-list'
import { CheckoutButton } from '../checkout-button'
import { OneClickButton } from '../one-click-button'
import {
  LawsuitTwoOfferPlanCard_OfferLockBodyFragment,
  LawsuitTwoOfferPlanCard_OfferLockCardFragment,
  LawsuitTwoOfferPlanCard_ViewerFragment,
} from './__generated__/lawsuit-two-offer-plan-card.graphql'
import { LawsuitPlanBenefits, LawsuitPlanPriceInfo } from './lawsuit-plan-card-content-inner'
import styles from './lawsuit-two-offer-plan-card.module.scss'

export interface LawsuitTwoOfferPlanCardProps extends HTMLAttributes<HTMLDivElement> {
  viewer: LawsuitTwoOfferPlanCard_ViewerFragment
  offerLockCard: LawsuitTwoOfferPlanCard_OfferLockCardFragment
  offerLockBody: LawsuitTwoOfferPlanCard_OfferLockBodyFragment
  className?: string
}

export function LawsuitTwoOfferPlanCard(props: LawsuitTwoOfferPlanCardProps) {
  const { offerLockCard, className, viewer } = props
  const cardClasses = classNames(styles['plan-offer-card'], className)
  const { title, offer } = offerLockCard
  const { offerLockBody } = props
  const isProfessional = title.toLowerCase().includes('profissionais')
  const isPromotional = !!offer?.priceTitle
  // Matches "9,90" only when it isn't preceded by another digit, so prices
  // like "R$ 19,90" don't accidentally trigger the experiment layout.
  const NO_PROMO_EXPERIMENT_PRICE_REGEX = /(?:^|\D)9,90/
  const lockHasPromoOffer = offerLockBody?.cards?.some((card) => !!card.offer?.priceTitle)
  const hasExperimentPrice =
    !!offer?.priceText && NO_PROMO_EXPERIMENT_PRICE_REGEX.test(offer.priceText)
  const isNoPromoLawsuitExperiment = !isPromotional && hasExperimentPrice && lockHasPromoOffer
  const showPriceFirst = isPromotional || isNoPromoLawsuitExperiment
  const hasCreditCard = !!offerLockCard.creditCard?.ccToken

  return (
    <Card className={cardClasses}>
      <Chip
        kind="secondary"
        leftIcon={isProfessional ? <ScalesIcon /> : <UserMultiIcon />}
        className={styles['two-offer-lock-card-chip']}
      >
        {title}
      </Chip>

      {showPriceFirst ? (
        <div className={styles['two-offer-lock-card-content']}>
          <LawsuitPlanPriceInfo offer={offer} reserveTitleSpace={isNoPromoLawsuitExperiment} />
          <div className={styles['two-offer-lock-card-buttons']}>
            <CheckoutButton offerLockCard={offerLockCard} kind="secondary" />
            {hasCreditCard && (
              <OneClickButton offerLockCard={offerLockCard} viewer={viewer} kind="secondary" />
            )}
          </div>
          <LawsuitPlanBenefits offerLockCard={offerLockCard} />
        </div>
      ) : (
        <>
          <div className={styles['two-offer-lock-card-content']}>
            <LawsuitPlanBenefits offerLockCard={offerLockCard} />
          </div>
          <LawsuitPlanPriceInfo offer={offer} />
          <div className={styles['two-offer-lock-card-buttons']}>
            <CheckoutButton offerLockCard={offerLockCard} kind="secondary" />
            <OneClickButton offerLockCard={offerLockCard} viewer={viewer} kind="secondary" />
          </div>
        </>
      )}
    </Card>
  )
}

LawsuitTwoOfferPlanCard.fragments = {
  offerLockCard: gql`
    fragment LawsuitTwoOfferPlanCard_offerLockCard on OfferLockCard {
      title
      subtitle
      offer {
        priceTitle
        priceText
        frequency
      }
      button {
        label
      }
      ...OneClickButton_offerLockCard
      ...CheckoutButton_offerLockCard
      ...BenefitsList_offerLockCard
    }
    ${OneClickButton.fragments.offerLockCard}
    ${CheckoutButton.fragments.offerLockCard}
    ${BenefitsList.fragments.offerLockCard}
  `,
  offerLockBody: gql`
    fragment LawsuitTwoOfferPlanCard_offerLockBody on OfferLockBody {
      cards {
        offer {
          priceTitle
          priceText
          frequency
        }
      }
    }
  `,
  viewer: gql`
    fragment LawsuitTwoOfferPlanCard_viewer on Profile {
      ...OneClickButton_viewer
    }
    ${OneClickButton.fragments.viewer}
  `,
}
